import React from "react";
import { FeatherChevronLeft, FeatherChevronRight } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasMore: boolean;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  totalItems,
  hasMore,
  itemsPerPage,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex w-full items-center justify-between mt-4 flex-shrink-0">
      <span className="text-body font-body text-subtext-color">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
        results
      </span>

      <div className="flex items-center gap-2">
        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          icon={<FeatherChevronLeft />}
        >
          Previous
        </Button>

        <div className="flex items-center gap-1">
          {getVisiblePages(currentPage, totalPages).map((pageNumber) => (
            <Button
              key={pageNumber}
              variant={
                currentPage === pageNumber ? "brand-primary" : "neutral-tertiary"
              }
              size="small"
              onClick={() => onPageChange(pageNumber)}
              className="min-w-[32px]"
            >
              {pageNumber}
            </Button>
          ))}
        </div>

        <Button
          variant="neutral-tertiary"
          size="small"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasMore}
          icon={<FeatherChevronRight />}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const getVisiblePages = (currentPage: number, totalPages: number) => {
  return Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
    if (totalPages <= 5) return index + 1;
    if (currentPage <= 3) return index + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + index;
    return currentPage - 2 + index;
  });
};
